import React, { useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * MobileDataCard — swipeable card row that replaces desktop table rows on
 * mobile. Left-swipe reveals up to two actions (approve/delete), tap on
 * body triggers the primary action.
 *
 * Props:
 *   title       required primary line
 *   subtitle    secondary line under title (usually the "what")
 *   badges      array of { label, tone } — tones: fuchsia/emerald/amber/red/neutral
 *   rows        array of { label, value, mono? } shown as a 2-col mini-grid
 *   onTap       primary tap handler (usually "edit" / "open")
 *   actions     array of { label, icon: LucideIcon, onClick, tone } — rendered
 *               under the card as a swipe-revealed action strip; also shown
 *               as a compact button row on hover for larger phones
 *   testId      data-testid for automation
 */
export default function MobileDataCard({
  title,
  subtitle,
  badges = [],
  rows = [],
  onTap,
  actions = [],
  testId,
  accent = 'neutral',
}) {
  const [offset, setOffset] = useState(0);
  const startX = useRef(null);

  const toneMap = {
    fuchsia: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    neutral: 'bg-neutral-100 text-neutral-800 border-neutral-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
  };

  const actionToneBg = {
    emerald: 'bg-emerald-500 hover:bg-emerald-600',
    red: 'bg-red-500 hover:bg-red-600',
    blue: 'bg-blue-500 hover:bg-blue-600',
    fuchsia: 'bg-fuchsia-500 hover:bg-fuchsia-600',
    neutral: 'bg-neutral-500 hover:bg-neutral-600',
  };

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchMove = (e) => {
    if (startX.current == null) return;
    const delta = e.touches[0].clientX - startX.current;
    // Only allow left swipe (delta negative), max reveal 160px
    setOffset(Math.max(Math.min(0, delta), -160));
  };
  const onTouchEnd = () => {
    // Snap: revealed if past halfway, otherwise closed
    setOffset(offset < -60 ? -160 : 0);
    startX.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-neutral-200 shadow-sm mb-2" data-testid={testId}>
      {/* Swipe-revealed actions layer (behind the card) */}
      {actions.length > 0 && (
        <div className="absolute inset-y-0 right-0 flex items-stretch">
          {actions.map((a, i) => {
            const Icon = a.icon;
            return (
              <button
                key={i}
                onClick={a.onClick}
                className={`w-20 flex flex-col items-center justify-center text-white text-[10px] font-semibold uppercase tracking-wider ${actionToneBg[a.tone] || actionToneBg.neutral}`}
                data-testid={a.testId}
              >
                {Icon && <Icon className="w-5 h-5 mb-0.5" />}
                {a.label}
              </button>
            );
          })}
        </div>
      )}
      {/* Foreground card content */}
      <div
        className="relative bg-white p-4 transition-transform duration-200"
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={onTap}
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-[#1a1a2e] truncate">{title}</div>
            {subtitle && <div className="text-xs text-neutral-600 truncate mt-0.5">{subtitle}</div>}
          </div>
          {onTap && <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />}
        </div>
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {badges.map((b, i) => (
              <span key={i} className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full border ${toneMap[b.tone] || toneMap.neutral}`}>
                {b.label}
              </span>
            ))}
          </div>
        )}
        {rows.length > 0 && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            {rows.map((r, i) => (
              <div key={i}>
                <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">{r.label}</div>
                <div className={`text-neutral-900 truncate ${r.mono ? 'font-mono tabular-nums font-semibold' : ''}`}>{r.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
